"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { branchSchema, BranchFormValues } from "./schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { AppButton } from "../ui/AppButton";
import { FormField2 } from "../form/FormField2";
import CustomPhoneInput from "../ui/phone-input";
import { useEffect, useState } from "react";

interface BranchFormDialogProps {
  mode?: "create" | "edit";
  defaultValues?: Partial<BranchFormValues>;
  onSubmit: (values: BranchFormValues) => Promise<void> | void;
  trigger?: React.ReactNode;
  open: boolean;
  setOpen:(value:boolean) => void;
}

export const BranchFormDialog = ({
  mode = "create",
  defaultValues,
  onSubmit,
  trigger,
  open,
  setOpen,
}: BranchFormDialogProps) => {
  const form = useForm<BranchFormValues>({
    resolver: zodResolver(branchSchema),
    defaultValues: defaultValues ?? {
      branchName: "",
      branchEmail: "",
      branchPhone: "",
      region: "",
      city: "",
      subCity: "",
      woreda: "",
      contactFullName: "",
      contactUsername: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = form;

    // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  // Handle form submission with reset
  const handleFormSubmit = async (data: BranchFormValues) => {
    try {
      await onSubmit(data);
      // Form will be reset by the useEffect when dialog closes
    } catch (error) {
      // Form will be reset by the useEffect when dialog closes
      console.error("Form submission error:", error);
    }
  };



  const title = mode === "create" ? "Create New Branch" : "Edit Branch";
  const subTitle =
    mode === "create"
      ? "Add a new branch to the system."
      : "Update branch information.";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-5xl bg-white p-10 !rounded-[30px] z-50">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="">
          <DialogHeader className="flex flex-row items-center justify-between border-b border-gray-200 pb-6">
            {/** Left side: Title and subtitle */}
            <div>
              <DialogTitle className="text-xl font-semibold text-[#09090B]">
                {title}
              </DialogTitle>
              <p className="text-sm text-[#505050] pt-2">{subTitle}</p>
            </div>

            {/** Right side: Buttons */}
            <div className="flex items-center gap-2">
              <AppButton
                type="button"
                variant="outline"
                className="px-6"
                onClick={() => setOpen(false)}
              >
                Cancel
              </AppButton>
              <AppButton
                variant="success"
                type="submit"
                disabled={isSubmitting}
                className="px-6 font-medium"
              >
                {mode === "create" ? "Create" : "Update"}
              </AppButton>
            </div>
          </DialogHeader>

          {/* Branch Information Section */}
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4 pt-6">
              <h3 className="text-base font-semibold text-gray-900/50">
                Branch Information
              </h3>
              <div className="!mt-0 pt-6">
                <FormField2
                  label="Branch Name"
                  name="branchName"
                  placeholder="Enter branch name"
                  register={register("branchName")}
                  error={errors.branchName}
                  required
                />

                <FormField2
                  label="Email"
                  name="branchEmail"
                  placeholder="Enter email address"
                  register={register("branchEmail")}
                  error={errors.branchEmail}
                  required
                  className="pt-4"
                />
              </div>

              <Controller
                name="branchPhone"
                control={control}
                render={({ field, fieldState }) => (
                  <CustomPhoneInput
                    label="Branch Phone"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField2
                    label="Region"
                    name="region"
                    as="select"
                    placeholder="Select Region"
                    options={[
                      { label: "Addis Ababa", value: "addis" },
                      { label: "Oromia", value: "oromia" },
                      { label: "Amhara", value: "amhara" }, 
                      { label: "Benishangul", value: "benishangul" }, 
                      { label: "Gambela", value: "gambela" }, 
                      { label: "Tigray", value: "tigray" }, 
                      { label: "SNNPE", value: "SNNPE" }, 
                    ]}
                    value={watch("region")}
                    onChange={(val) => setValue("region", val)}
                    error={errors.region}
                  />

                  <FormField2
                    label="City"
                    name="city"
                    placeholder="Select City"
                    value={"city"}
                    onChange={(val) => setValue("city", val)}
                    error={errors.city}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField2
                    label="Sub-City"
                    name="subCity"
                    placeholder="Select Sub-City"
                    value={"subCity"}
                    onChange={(val) => setValue("subCity", val)}
                    error={errors.subCity}
                  />
                  <FormField2
                    label="Woreda"
                    name="woreda"
                    placeholder="Select Woreda"
                    value={"woreda"}
                    onChange={(val) => setValue("woreda", val)}
                    error={errors.woreda}
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            {/* Contact Person Information Section */}
            <div className="pt-6 space-y-4">
              <h3 className="text-base font-semibold text-gray-900/50">
                Contact Person Information
              </h3>

              <div className="pt-6 !mt-0">
                {/* Left Column */}
                <div className="space-y-4">
                  <FormField2
                    label="Full Name"
                    name="contactFullName"
                    placeholder="Enter full name"
                    register={register("contactFullName")}
                    error={errors.contactFullName}
                    required
                  />
                  <FormField2
                    label="Username"
                    name="contactUsername"
                    placeholder="Enter username"
                    register={register("contactUsername")}
                    error={errors.contactUsername}
                    required
                  />

                  <FormField2
                    label="Email"
                    name="contactEmail"
                    placeholder="Enter email address"
                    register={register("contactEmail")}
                    error={errors.contactEmail}
                    required
                  />

                  <Controller
                    name="contactPhone"
                    control={control}
                    render={({ field, fieldState }) => (
                      <CustomPhoneInput
                        label="Contact Phone"
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer section */}
        </form>
      </DialogContent>
    </Dialog>
  );
};
